'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdSave,
    MdDescription,
    MdAdd,
    MdDelete,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createPrescription,
    updatePrescription,
    getPrescription,
    CreatePrescriptionData,
    UpdatePrescriptionData,
    CreatePrescriptionItemData,
} from '@/lib/api/prescriptions/prescription-api'
import { ClientSelector } from '@/components/client/ClientSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'

interface MedicationItem {
    medication: string
    dosage: string
    frequency: string
    duration: string
    instructions: string
    quantity: string
}

interface FormData {
    content: string
    observations: string
    validUntil: string
    clientId: string
    /**
     * Quem ASSINA a receita — não quem a digita.
     *
     * A secretária monta a receita pelo médico, como já fazia com o atestado. Quem
     * está logado como profissional tem o campo preenchido e não precisa tocá-lo.
     */
    professionalId: string
}

export default function PrescriptionFormPage() {
    const { user, isProfessional } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [medications, setMedications] = useState<MedicationItem[]>([])

    const [formData, setFormData] = useState<FormData>({
        content: '',
        observations: '',
        validUntil: '',
        clientId: '',
        professionalId: '',
    })

    useEffect(() => {
        if (user?.companyId) {
            if (isEditing && id) {
                loadPrescription()
            } else if (isProfessional && user?.professionalId) {
                // O médico logado não escolhe a si mesmo a cada receita.
                setFormData(prev => ({
                    ...prev,
                    professionalId: String(user.professionalId),
                }))
            }
        }
    }, [id, isEditing, user?.companyId, isProfessional, user?.professionalId])

    const loadPrescription = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getPrescription(parseInt(id, 10))
            if (response.success && response.result) {
                const prescription = response.result
                setFormData({
                    content: prescription.content || '',
                    observations: prescription.observations || '',
                    validUntil: prescription.validUntil
                        ? prescription.validUntil.split('T')[0]
                        : '',
                    clientId: prescription.clientId?.toString() || '',
                    professionalId:
                        prescription.professionalId?.toString() || '',
                })

                if (prescription.items && prescription.items.length > 0) {
                    setMedications(
                        prescription.items.map(item => ({
                            medication: item.medication || '',
                            dosage: item.dosage || '',
                            frequency: item.frequency || '',
                            duration: item.duration || '',
                            instructions: item.instructions || '',
                            quantity: item.quantity?.toString() || '',
                        }))
                    )
                }
            } else {
                toast.error(response.error || 'Erro ao carregar receita')
                navigate('/medical/prescriptions')
            }
        } catch (error) {
            toast.error('Erro ao carregar receita')
            navigate('/medical/prescriptions')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const addMedication = () => {
        setMedications(prev => [
            ...prev,
            {
                medication: '',
                dosage: '',
                frequency: '',
                duration: '',
                instructions: '',
                quantity: '',
            },
        ])
    }

    const removeMedication = (index: number) => {
        setMedications(prev => prev.filter((_, i) => i !== index))
    }

    const updateMedication = (
        index: number,
        field: keyof MedicationItem,
        value: string
    ) => {
        setMedications(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        )
    }

    const validateForm = (): boolean => {
        if (!formData.content.trim()) {
            toast.error('Conteúdo da receita é obrigatório')
            return false
        }
        if (!formData.clientId) {
            toast.error('Selecione um paciente')
            return false
        }
        if (!formData.professionalId) {
            toast.error('Selecione o profissional que assina a receita')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            const items: CreatePrescriptionItemData[] = medications
                .filter(m => m.medication.trim())
                .map(m => ({
                    medication: m.medication.trim(),
                    dosage: m.dosage.trim() || undefined,
                    frequency: m.frequency.trim() || undefined,
                    duration: m.duration.trim() || undefined,
                    instructions: m.instructions.trim() || undefined,
                    quantity: m.quantity ? parseInt(m.quantity, 10) : undefined,
                }))

            if (isEditing && id) {
                const updateData: UpdatePrescriptionData = {
                    content: formData.content.trim(),
                    observations: formData.observations.trim() || undefined,
                    validUntil: formData.validUntil || undefined,
                    items: items.length > 0 ? items : undefined,
                }

                const response = await updatePrescription(
                    parseInt(id, 10),
                    updateData
                )
                if (response.success) {
                    toast.success('Receita atualizada com sucesso!')
                    navigate(`/medical/prescriptions/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar receita')
                }
            } else {
                const createData: CreatePrescriptionData = {
                    content: formData.content.trim(),
                    observations: formData.observations.trim() || undefined,
                    validUntil: formData.validUntil || undefined,
                    clientId: parseInt(formData.clientId, 10),
                    professionalId: parseInt(formData.professionalId, 10),
                    companyId: user.companyId,
                    items: items.length > 0 ? items : undefined,
                }

                const response = await createPrescription(createData)
                if (response.success && response.result) {
                    toast.success('Receita criada com sucesso!')
                    navigate(`/medical/prescriptions/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar receita')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar receita')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados da receita...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/medical/prescriptions')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdDescription className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Receita' : 'Nova Receita'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações da receita'
                                : 'Preencha os dados da nova receita'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações da Receita
                        </h2>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Paciente{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <ClientSelector
                                value={formData.clientId ? Number(formData.clientId) : null}
                                onChange={(id) => updateFormField('clientId', id ? String(id) : '')}
                                label=""
                                placeholder="Selecione..."
                                disabled={isEditing}
                            />
                        </div>

                        {/* Quem ASSINA. Fica escondido para o próprio médico, que já
                            vem preenchido — um campo com uma opção só, sempre a mesma,
                            é ruído em cima de quem emite receita o dia inteiro. */}
                        {!isProfessional && (
                            <div className="form-control w-full mt-4">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Profissional{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <ProfessionalSearchSelector
                                    value={
                                        formData.professionalId
                                            ? Number(formData.professionalId)
                                            : null
                                    }
                                    onChange={id =>
                                        updateFormField(
                                            'professionalId',
                                            id ? String(id) : ''
                                        )
                                    }
                                    label=""
                                    placeholder="Quem assina esta receita..."
                                    disabled={isEditing}
                                />
                            </div>
                        )}

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Validade
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={formData.validUntil}
                                onChange={e =>
                                    updateFormField(
                                        'validUntil',
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Conteúdo{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Conteúdo da receita..."
                                value={formData.content}
                                onChange={e =>
                                    updateFormField('content', e.target.value)
                                }
                                rows={4}
                                required
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Observações adicionais..."
                                value={formData.observations}
                                onChange={e =>
                                    updateFormField(
                                        'observations',
                                        e.target.value
                                    )
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="card-title text-lg">
                                Medicamentos
                            </h2>
                            <button
                                type="button"
                                onClick={addMedication}
                                className="btn btn-sm btn-secondary gap-2"
                            >
                                <MdAdd className="text-lg" />
                                Adicionar
                            </button>
                        </div>

                        {medications.length === 0 ? (
                            <div className="text-center py-8 text-base-content/60">
                                <p>Nenhum medicamento adicionado.</p>
                                <p className="text-sm mt-2">
                                    Clique em "Adicionar" para incluir
                                    medicamentos.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {medications.map((med, index) => (
                                    <div
                                        key={index}
                                        className="bg-base-200/50 rounded-lg p-4 border border-base-300/50"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Medicamento
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full"
                                                    placeholder="Nome do medicamento"
                                                    value={med.medication}
                                                    onChange={e =>
                                                        updateMedication(
                                                            index,
                                                            'medication',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Dosagem
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full"
                                                    placeholder="Ex: 500mg"
                                                    value={med.dosage}
                                                    onChange={e =>
                                                        updateMedication(
                                                            index,
                                                            'dosage',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Frequência
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full"
                                                    placeholder="Ex: 8 em 8 horas"
                                                    value={med.frequency}
                                                    onChange={e =>
                                                        updateMedication(
                                                            index,
                                                            'frequency',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Duração
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full"
                                                    placeholder="Ex: 7 dias"
                                                    value={med.duration}
                                                    onChange={e =>
                                                        updateMedication(
                                                            index,
                                                            'duration',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Quantidade
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="input input-bordered w-full"
                                                    placeholder="Quantidade"
                                                    value={med.quantity}
                                                    onChange={e =>
                                                        updateMedication(
                                                            index,
                                                            'quantity',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className="form-control w-full md:col-span-2">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Instruções
                                                    </span>
                                                </label>
                                                <textarea
                                                    className="textarea textarea-bordered w-full"
                                                    placeholder="Instruções de uso..."
                                                    value={med.instructions}
                                                    onChange={e =>
                                                        updateMedication(
                                                            index,
                                                            'instructions',
                                                            e.target.value
                                                        )
                                                    }
                                                    rows={2}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex justify-end mt-4">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeMedication(index)
                                                }
                                                className="btn btn-sm btn-error btn-outline gap-2"
                                            >
                                                <MdDelete className="text-lg" />
                                                Remover
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/medical/prescriptions')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
